import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Space, Input, notification, Checkbox } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Variable } from '@/types/TVariables';
import { removeVariable, setVariable } from '@/store/novi/variables/slice';
import { getAllVariables } from '@/store/novi/variables/selector';
import VariableList from './VariableList';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { updateCampaignVariables } from '@/api/novi/CamapaignPortalApis';
import { setSelectedAgent } from '@/store/account/slice';
import { updateUser } from '@/api/user';
import { BsLightningCharge } from 'react-icons/bs';
import { getOutboundAgents, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { User } from '@/types/TAccounts';
import { safeJSONParse } from '@/utils/helper';

const VariablesManager: React.FC = () => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();
	const variables = useAppSelector(getAllVariables);
	const outboundAgents = useAppSelector(getOutboundAgents);
	const selectedCampaign = useAppSelector(getSelectedAgent);

	const [modalVisible, setModalVisible] = useState(false);
	const [variableName, setVariableName] = useState('');
	const [variableValue, setVariableValue] = useState('');
	const [editingVariable, setEditingVariable] = useState<string | null>(null);
	const [isMandatory, setIsMandatory] = useState(true);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const { mutateAsync: updateCampaignFieldAsync } = useMutation({
		mutationKey: ['update-query'],
		mutationFn: updateUser,
	});

	const { mutate: updateVariables } = useMutation({
		mutationKey: ['update-variables'],
		mutationFn: () =>
			updateCampaignVariables(selectedCampaign?.number as string, variables, 'Client'),
		onSuccess: () => {
			notification.success({ message: 'Variables updated successfully!', duration: 3 });
			queryClient.invalidateQueries({ queryKey: ['get-agents'] });
		},
	});

	useEffect(() => {
		if (outboundAgents) {
			const updatedCampaign = outboundAgents.find(
				(campaign: any) => campaign.number === selectedCampaign?.number,
			);

			if (updatedCampaign) {
				dispatch(setSelectedAgent({ user: updatedCampaign }));
			}
		}
	}, [dispatch]);

	useEffect(() => {
		if (selectedCampaign?.variables) {
			try {
				const parsedVariables = safeJSONParse(selectedCampaign.variables, {});
				Object.entries(parsedVariables).forEach(([key, value]) => {
					dispatch(setVariable({ name: key, data: value as Variable }));
				});
			} catch (error) {
				console.error('Error setting variables:', error);
			}
		}
	}, [dispatch]);

	const handleDeleteVariable = (name: string) => {
		const variablePattern = new RegExp(`{{${name}}}`, 'g');
		const fieldsUsingVariable = selectedCampaign
			? Object.entries(selectedCampaign)
					.filter(([_, value]) => typeof value === 'string' && variablePattern.test(value))
					.map(([field]) => field)
			: [];

		if (fieldsUsingVariable.length > 0) {
			Modal.confirm({
				title: 'Variable in use',
				content: `This variable is used in: ${fieldsUsingVariable.join(', ')}. Delete anyway?`,
				onOk: async () => {
					try {
						dispatch(removeVariable(name));
						await updateVariables();

						const updatePromises = fieldsUsingVariable.map(async (field) => {
							const updatedValue = (selectedCampaign as User)?.[field as keyof User].replace(
								variablePattern,
								'',
							);
							await updateCampaignFieldAsync({
								[field]: updatedValue,
								agentId: selectedAgentId as string,
							});
						});

						await Promise.all(updatePromises);

						notification.success({
							message: 'Variable and related fields updated successfully!',
							duration: 3,
						});

						queryClient.invalidateQueries({ queryKey: ['get-agents'] });
					} catch {
						notification.error({
							message: 'Failed to update some fields',
							description: 'Please try again or contact support.',
							duration: 3,
						});
					}
				},
			});
		} else {
			dispatch(removeVariable(name));
			updateVariables();
			notification.success({
				message: 'Variable deleted successfully!',
				duration: 3,
			});
		}
	};

	const openModal = useCallback((variable?: { name: string; data: Variable }) => {
		if (variable) {
			setEditingVariable(variable.name);
			setVariableValue(variable.data.defaultValue || '');
			setIsMandatory(variable.data.isMandatory);
		} else {
			setEditingVariable(null);
			setVariableName('');
			setVariableValue('');
			setIsMandatory(true);
		}
		setModalVisible(true);
	}, []);

	const handleSaveVariable = () => {
		if (!variableName.trim() && !editingVariable) {
			return;
		}
		if (!isMandatory && !variableValue.trim()) {
			return;
		}

		const variableData: Variable = {
			isMandatory,
			defaultValue: isMandatory ? '' : variableValue,
		};

		if (editingVariable) {
			dispatch(setVariable({ name: editingVariable, data: variableData }));
		} else {
			dispatch(setVariable({ name: variableName, data: variableData }));
		}

		setModalVisible(false);
		updateVariables();
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/30 p-4 sm:p-6 lg:p-8">
			<div className="max-w-6xl mx-auto">
				<div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border border-slate-100">
					{/* Header Section */}
					<div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-r from-indigo-500/5 to-purple-500/5 border-b border-slate-100">
						<div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
							<div className="p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl shadow-sm self-start">
								<BsLightningCharge className="text-2xl sm:text-3xl text-indigo-600" />
							</div>
							<div>
								<h1 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">
									Variable Management
								</h1>
								<p className="text-sm sm:text-base text-slate-600 leading-relaxed">
									Create and manage dynamic variables to personalize your content. Use the format{' '}
									<code className="px-1.5 py-0.5 bg-white rounded-md text-indigo-600 font-mono text-sm shadow-sm inline-flex items-center">
										{'{{'}variableName{'}}'}
									</code>{' '}
									to insert variables in your text.
								</p>
							</div>
						</div>
					</div>

					{/* Content Section */}
					<div className="p-4 sm:p-6 lg:p-8">
						<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-6">
							<div>
								<h2 className="text-lg sm:text-xl font-semibold text-gray-800">Your Variables</h2>
								<p className="text-sm text-slate-500">
									{Object.keys(variables).length} variables configured
								</p>
							</div>
							<Button
								type="primary"
								icon={<BsLightningCharge className="text-lg" />}
								onClick={() => openModal()}
								className="!w-full sm:!w-auto !flex !items-center !justify-center !gap-2 !bg-gradient-to-r 
                         !from-indigo-500 !to-purple-500 !border-0 !h-10 sm:!h-11 !px-4 sm:!px-6 
                         hover:!from-indigo-600 hover:!to-purple-600 !shadow-md !rounded-xl"
							>
								Add Variable
							</Button>
						</div>

						<VariableList
							variables={variables}
							onEdit={(name, data) => openModal({ name, data })}
							onDelete={handleDeleteVariable}
						/>
					</div>
				</div>
			</div>

			<Modal
				title={
					<div className="flex items-center gap-3 py-2">
						<div className="p-2.5 bg-gradient-to-tr from-indigo-500/10 to-purple-500/10 rounded-xl">
							<BsLightningCharge className="text-indigo-600 text-lg" />
						</div>
						<span className="text-xl font-semibold">
							{editingVariable ? 'Edit Variable' : 'Create New Variable'}
						</span>
					</div>
				}
				open={modalVisible}
				onCancel={() => setModalVisible(false)}
				onOk={handleSaveVariable}
				okText={editingVariable ? 'Update Variable' : 'Create Variable'}
				okButtonProps={{
					disabled: !editingVariable && !variableName.trim(),
					className: '!bg-gradient-to-r !from-indigo-500 !to-purple-500 !border-0',
				}}
				width={500}
				className="[&_.ant-modal-content]:!rounded-2xl [&_.ant-modal-header]:!border-b [&_.ant-modal-header]:!border-slate-100 
                   [&_.ant-modal-footer]:!border-t [&_.ant-modal-footer]:!border-slate-100"
			>
				<div className="p-6">
					<Space direction="vertical" className="w-full gap-4">
						{!editingVariable && (
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Variable Name
								</label>
								<Input
									placeholder="Enter a unique identifier"
									value={variableName}
									onChange={(e) => {
										const newName = e.target.value;

										setVariableName(newName);
									}}
									className="!rounded-lg !border-slate-200"
								/>
							</div>
						)}

						<div>
							<Checkbox
								checked={isMandatory}
								onChange={(e) => setIsMandatory(e.target.checked)}
								className="mb-4"
							>
								<span className="text-sm font-medium text-gray-700">
									This variable is mandatory
								</span>
							</Checkbox>

							{!isMandatory && (
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Default Value
									</label>
									<Input
										placeholder="Enter the default value"
										value={variableValue}
										onChange={(e) => setVariableValue(e.target.value)}
										className="!rounded-lg !border-slate-200"
									/>
								</div>
							)}
						</div>
					</Space>
				</div>
			</Modal>
		</div>
	);
};

export default VariablesManager;
